'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@/lib/utils';
import { SpeechInput } from '@/components/ai-elements/speech-input';

// ============================================================================
// Constants
// ============================================================================

const PLACEHOLDER_EXAMPLES = [
  '3 bedroom apartment in Dubai Marina under 5 million',
  'Family villa with a garden in Dubai Hills',
  'Penthouse near Downtown with a view',
  'Waterfront living on Palm Jumeirah',
  'New launch apartments under 3 million AED',
  'Spacious villa for a large family, 4+ bedrooms',
];

const QUICK_CHIPS = [
  { label: 'Villas under 10M', query: 'Villas under 10 million' },
  { label: '3BR in Marina', query: '3 bedroom apartment in Dubai Marina' },
  { label: 'New Launches', query: 'New launch properties' },
  { label: 'Waterfront homes', query: 'Waterfront properties near the beach' },
];

const PLACEHOLDER_INTERVAL = 4000; // 4 seconds

// ============================================================================
// Component
// ============================================================================

interface PropertySearchBarProps {
  initialQuery?: string | null;
  isSearching?: boolean;
  autoFocus?: boolean;
  onSearch?: (query: string) => void;
  onQueryChange?: (query: string) => void;
}

export function PropertySearchBar({
  initialQuery,
  isSearching = false,
  autoFocus = false,
  onSearch,
  onQueryChange,
}: PropertySearchBarProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const placeholderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [query, setQuery] = useState(initialQuery ?? '');
  const [isFocused, setIsFocused] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [showPlaceholder, setShowPlaceholder] = useState(true);

  // Sync internal state when initialQuery prop changes (e.g. cleared on cannot_parse)
  useEffect(() => {
    setQuery(initialQuery ?? '');
  }, [initialQuery]);

  // Auto-focus when ?focus=search is in URL
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Rotate placeholder text
  useEffect(() => {
    if (isFocused || query) return; // Pause when focused or has text

    const interval = setInterval(() => {
      setShowPlaceholder(false);
      placeholderTimeoutRef.current = setTimeout(() => {
        setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_EXAMPLES.length);
        setShowPlaceholder(true);
      }, 300); // Crossfade gap
    }, PLACEHOLDER_INTERVAL);

    return () => {
      clearInterval(interval);
      if (placeholderTimeoutRef.current) {
        clearTimeout(placeholderTimeoutRef.current);
        placeholderTimeoutRef.current = null;
      }
    };
  }, [isFocused, query]);

  // Submit search
  const handleSubmit = useCallback(
    (searchQuery?: string) => {
      const q = (searchQuery ?? query).trim();
      if (!q) return;

      if (onSearch) {
        // Let parent handle navigation (enables useTransition loading state)
        onSearch(q);
      } else {
        // Fallback: navigate directly
        const params = new URLSearchParams(searchParams.toString());
        params.set('q', q);
        router.push(`/properties?${params.toString()}`);
      }
    },
    [query, router, searchParams, onSearch]
  );

  // Clear search
  const handleClear = useCallback(() => {
    setQuery('');
    router.push('/properties');
    inputRef.current?.focus();
  }, [router]);

  // Handle keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Voice input callback
  const handleVoiceTranscription = useCallback(
    (text: string) => {
      setQuery(text);
      // Auto-submit after voice input
      handleSubmit(text);
    },
    [handleSubmit]
  );

  // Chip click
  const handleChipClick = (chipQuery: string) => {
    setQuery(chipQuery);
    handleSubmit(chipQuery);
  };

  const showChips = isFocused && !query && !isSearching;
  const showClear = query.length > 0;
  const showSubmitArrow = query.length > 0;

  return (
    <div className="w-full max-w-2xl mx-auto space-y-4">
      {/* Eyebrow label */}
      <div className="text-center">
        <span className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">
          Describe your ideal home
        </span>
      </div>

      {/* Search input container */}
      <div
        className={cn(
          'relative flex items-center w-full h-14 rounded-xl border bg-background transition-all duration-300',
          isSearching
            ? 'border-secondary/40 animate-shimmer'
            : isFocused
              ? 'border-secondary/60 ring-1 ring-ring shadow-lg'
              : 'border-primary/20'
        )}
      >
        {/* Sparkles icon */}
        <div className="flex-shrink-0 pl-4">
          <Sparkles className="w-4 h-4 text-secondary" />
        </div>

        {/* Input field */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onQueryChange?.(e.target.value);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            // Delay blur to allow chip clicks
            setTimeout(() => setIsFocused(false), 200);
          }}
          onKeyDown={handleKeyDown}
          readOnly={isSearching}
          enterKeyHint="search"
          autoComplete="off"
          className={cn(
            'flex-1 h-full bg-transparent px-3 font-sans text-base text-primary',
            'placeholder:text-muted-foreground',
            'focus:outline-none focus:ring-0',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
          placeholder={
            isSearching
              ? 'Finding matching properties...'
              : showPlaceholder
                ? PLACEHOLDER_EXAMPLES[placeholderIndex]
                : ''
          }
        />

        {/* Right-side actions */}
        <div className="flex-shrink-0 flex items-center gap-1 pr-2">
          {/* Clear button */}
          {showClear && !isSearching && (
            <button
              type="button"
              onClick={handleClear}
              className="p-2 text-muted-foreground hover:text-primary transition-colors"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Submit arrow (when text present) */}
          {showSubmitArrow && !isSearching && (
            <button
              type="button"
              onClick={() => handleSubmit()}
              className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              aria-label="Search properties"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          )}

          {/* Microphone (when input is empty) */}
          {!showSubmitArrow && !isSearching && (
            <SpeechInput
              onTranscriptionChange={handleVoiceTranscription}
              className="h-8 w-8 bg-transparent text-muted-foreground hover:text-primary hover:bg-transparent"
              size="icon-sm"
            />
          )}
        </div>
      </div>

      {/* Inline guidance hint for vague queries */}
      {/* This is shown externally via the searchContext state */}

      {/* Focus-reveal example chips */}
      <AnimatePresence>
        {showChips && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="flex flex-wrap justify-center gap-2 pt-1"
          >
            {QUICK_CHIPS.map((chip) => (
              <button
                key={chip.label}
                type="button"
                onMouseDown={(e) => {
                  // Prevent input blur before chip click registers
                  e.preventDefault();
                  handleChipClick(chip.query);
                }}
                className={cn(
                  'border border-primary/20 rounded-xl px-4 py-2',
                  'text-xs tracking-wider text-muted-foreground',
                  'hover:border-secondary hover:text-secondary',
                  'transition-all duration-200 cursor-pointer'
                )}
              >
                {chip.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
